import { useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  LuMap,
  LuFolderKanban,
  LuChartBar,
  LuLayers,
  LuFileText,
  LuSettings,
  LuUsers,
  LuMenu,
  LuLogOut,
  LuBookOpen,
} from 'react-icons/lu'
import { useUIStore, useAuthStore } from '@stores/index'
import { cn } from '@lib/cn'

import { BRANDING } from '@/config/branding'
import kcsLogo from '@/assets/kcs_logo.png'
import { usePermission } from '@/hooks/usePermission'
import { NAV_ITEMS } from '@/config/permissions'

// Icon mapping for nav items (since config doesn't store React components)
const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  '/dashboard': LuMap,
  '/portfolio': LuFolderKanban,
  '/analytics': LuChartBar,
  '/layers': LuLayers,
  '/reports': LuFileText,
  '/docs': LuBookOpen,
  '/settings': LuSettings,
  '/settings/users': LuUsers,
}

export function Sidebar() {
  const location = useLocation()
  const { sidebarCollapsed, toggleSidebar } = useUIStore()
  const { user, logout } = useAuthStore()
  const { canAccess } = usePermission()

  // Filter navigation items based on permissions
  // Uses canAccess to show items where user has ANY permission (including :own)
  const visibleNavItems = useMemo(() => {
    return NAV_ITEMS.filter((item) => canAccess(item.resource, item.action)).map((item) => ({
      ...item,
      icon: iconMap[item.path] || LuSettings,
    }))
  }, [canAccess])

  return (
    <>
      {/* Mobile Backdrop */}
      {!sidebarCollapsed && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 md:hidden"
          onClick={toggleSidebar}
          aria-hidden="true"
        />
      )}

      <aside
        className={cn(
          'fixed left-0 top-0 h-screen bg-[var(--color-bg-secondary)] border-r border-[var(--color-border)] z-50',
          'transition-transform md:transition-all duration-300 flex flex-col',
          // Mobile: Toggle visibility via transform
          sidebarCollapsed ? '-translate-x-full md:translate-x-0' : 'translate-x-0',
          // Desktop: Toggle width
          sidebarCollapsed ? 'md:w-20' : 'w-60'
        )}
      >
        {/* Logo & Toggle */}
        <div className={cn(
          "h-16 flex items-center border-b border-[var(--color-border)] relative group",
          sidebarCollapsed ? "justify-center px-0 gap-1" : "justify-between px-4"
        )}>
          {!sidebarCollapsed ? (
            <Link to="/" className="flex items-center gap-3.5 transition-opacity hover:opacity-80">
              {/* Logo with subtle glow */}
              <div className="relative flex-shrink-0">
                <div className="absolute inset-0 bg-brand/30 blur-md rounded-full scale-0 group-hover:scale-110 transition-transform duration-500 opacity-60" />
                <img src={kcsLogo} alt="Logo" className="relative w-9 h-9 object-contain drop-shadow-sm transition-transform duration-300 group-hover:scale-105" />
              </div>

              {/* Typography hierarchy */}
              <div className="flex flex-col justify-center">
                <span className="text-[14px] font-bold text-[var(--color-text-primary)] leading-none tracking-tight">
                  {BRANDING.APP_NAME.split(' ')[0]}
                </span>
                <span className="text-[9px] font-bold text-brand uppercase tracking-[0.15em] leading-tight mt-1">
                  {BRANDING.APP_NAME.split(' ').slice(1).join(' ')}
                </span>
              </div>
            </Link>
          ) : (
            <Link to="/" className="flex justify-center transition-opacity hover:opacity-80 shrink-0">
              <img src={kcsLogo} alt="Logo" className="w-5 h-5 object-contain drop-shadow-sm brightness-110" />
            </Link>
          )}
          <button
            onClick={toggleSidebar}
            className={cn(
              "hover:bg-[var(--color-surface)] text-[var(--color-text-muted)] hover:text-brand rounded-lg transition-all duration-200",
              sidebarCollapsed ? "p-2" : "p-3"
            )}
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <LuMenu className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1" role="navigation">
          {visibleNavItems.length === 0 ? (
            <div className="p-4 text-sm text-[var(--color-text-muted)]">No accessible pages</div>
          ) : (
            visibleNavItems.map(({ path, icon: Icon, label }) => {
              // Use startsWith for paths with nested routes (e.g., /portfolio/:id)
              const isActive = location.pathname === path || location.pathname.startsWith(`${path}/`)
              return (
                <Link
                  key={path}
                  to={path}
                  className={cn(
                    'group relative flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-300',
                    'overflow-hidden',
                    isActive
                      ? 'bg-gradient-to-r from-sky-500/10 to-blue-600/5 text-sky-400 font-medium'
                      : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-surface)]'
                  )}
                  aria-current={isActive ? 'page' : undefined}
                >
                  {/* Active Indicator Bar */}
                  {isActive && (
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-sky-500 rounded-r-full shadow-[0_0_8px_rgba(14,165,233,0.5)]" />
                  )}

                  {/* Icon with Glow Effect - color transition instead of scale to avoid layout shift */}
                  <div className={cn(
                    "relative flex-shrink-0",
                    isActive && "drop-shadow-[0_0_6px_rgba(56,189,248,0.4)]"
                  )}>
                    <Icon className={cn("w-5 h-5 transition-colors duration-300", isActive ? "text-sky-400" : "group-hover:text-sky-400")} />
                  </div>

                  {/* Label */}
                  {!sidebarCollapsed && (
                    <span className="relative z-10 tracking-wide transition-transform duration-300 group-hover:translate-x-1">
                      {label}
                    </span>
                  )}

                  {/* Subtle Shine Effect on Hover */}
                  {!isActive && (
                    <div className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                  )}
                </Link>
              )
            })
          )}
        </nav>

        {/* User section */}
        {/* User section - UI UX Pro Max Upgrade */}
        <div className="p-3 border-t border-[var(--color-border)]">
          <div className="group relative flex items-center gap-3 p-2 rounded-xl transition-all duration-300 hover:bg-[var(--color-surface)] cursor-default">

            {/* Visual Avatar with simplified gradient - opacity transition instead of scale */}
            <div className={cn(
              "w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm",
              "bg-gradient-to-br from-sky-400 to-blue-600 text-white font-semibold text-sm",
              "transition-opacity duration-300 group-hover:opacity-90"
            )}>
              {user?.username?.[0]?.toUpperCase() || 'U'}
            </div>

            {!sidebarCollapsed && (
              <>
                <div className="flex-1 min-w-0 flex flex-col justify-center">
                  <p className="text-[13px] font-semibold text-[var(--color-text-primary)] truncate leading-tight group-hover:text-sky-500 transition-colors">
                    {user?.username || 'User'}
                  </p>
                  <p className="text-[11px] text-[var(--color-text-tertiary)] truncate leading-tight mt-0.5 font-medium">
                    {user?.email || 'user@example.com'}
                  </p>
                </div>

                <button
                  onClick={logout}
                  className="p-1.5 rounded-lg text-[var(--color-text-muted)] hover:text-red-400 hover:bg-red-400/10 transition-all duration-200 opacity-0 group-hover:opacity-100 translate-x-2 group-hover:translate-x-0"
                  title="Logout"
                  aria-label="Logout"
                >
                  <LuLogOut className="w-4 h-4" />
                </button>
              </>
            )}
          </div>
        </div>
      </aside>
    </>
  )
}
